import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/ecommerce-freelancer";
const TITLE = "E-Commerce Freelancer beauftragen: Auswahl, Kosten, Ablauf";
const DESCRIPTION =
  "Was ein E-Commerce Freelancer übernimmt, wie sich Stundensätze und Festpreise unterscheiden und welche fünf Fragen vor der Beauftragung Klarheit schaffen.";
const UPDATED = "2026-09-12";

const ANSWER =
  "Ein E-Commerce Freelancer übernimmt Aufbau, Weiterentwicklung und Optimierung eines Onlineshops als direkter Ansprechpartner ohne Zwischenebene. Üblich sind Stundensätze im mittleren zweistelligen bis dreistelligen Bereich, bei mir 110 € netto pro Stunde, sowie Festpreise für abgegrenzte Vorhaben. Entscheidend für die Auswahl ist nachweisbare Erfahrung genau in Ihrem Shopsystem.";

const faqs = [
  {
    q: "Was macht ein E-Commerce Freelancer?",
    a: "Je nach Schwerpunkt Shopaufbau, Theme-Entwicklung, Schnittstellen, Migrationen oder Conversion-Optimierung. Gemeinsam ist die direkte Zusammenarbeit ohne Projektmanagement-Ebene dazwischen.",
  },
  {
    q: "Freelancer oder Agentur?",
    a: "Eine Agentur bietet Kapazität und mehrere Rollen, kostet aber Overhead. Ein Freelancer ist direkter und meist günstiger, hat dafür begrenzte Kapazität. Für klar umrissene Vorhaben ist das ein Vorteil.",
  },
  {
    q: "Was kostet ein E-Commerce Freelancer?",
    a: "Die Spanne ist groß und hängt an Erfahrung und Spezialisierung. Ich arbeite mit 110 € netto pro Stunde für Entwicklung und Anpassungen, Conversion-Projekte beginnen bei 3.990 € netto als Festpreis.",
  },
  {
    q: "Welche Fragen sollte ich vor der Beauftragung stellen?",
    a: "Welche vergleichbaren Projekte gibt es? Wie wird der Aufwand geschätzt? Wem gehört der Code? Wie läuft die Übergabe? Und was passiert, wenn nach Abschluss etwas nicht funktioniert?",
  },
  {
    q: "Wie läuft die Zusammenarbeit ab?",
    a: "Kurzes Gespräch zur Ausgangslage, Sichtung des Shops, schriftlicher Rahmen mit Aufwand und Ablauf, dann Umsetzung mit Zwischenstand. Sie sprechen dabei durchgehend mit derselben Person.",
  },
];

export const Route = createFileRoute("/ratgeber/ecommerce-freelancer")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Zusammenarbeit",
      updated: UPDATED,
    }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Zusammenarbeit"
      title="E-Commerce Freelancer: worauf es bei der Auswahl ankommt"
      intro="Der Begriff deckt sehr unterschiedliche Profile ab, von Marketing über Entwicklung bis Betrieb. Dieser Überblick hilft dabei, das passende Profil zu erkennen und die Zusammenarbeit sauber aufzusetzen."
      source="ratgeber-ecommerce-freelancer"
      answer={ANSWER}
      updated={UPDATED}
      sections={[
        {
          h: "Drei Profile hinter einem Begriff",
          p: [
            "Ein Teil kommt aus dem Marketing und arbeitet an Kampagnen und Reichweite. Ein Teil kommt aus der Entwicklung und baut Shops, Themes und Schnittstellen. Ein Teil arbeitet operativ am Betrieb mit Sortiment, Prozessen und Logistik.",
            "Die Profile überschneiden sich selten so weit, wie Profile es versprechen. Klären Sie zuerst, welches Problem gelöst werden soll, dann fällt die Auswahl leicht.",
          ],
        },
        {
          h: "Freelancer oder Agentur",
          p: [
            "Eine Agentur bringt Kapazität und mehrere Rollen mit, finanziert aber auch Struktur. Ein Freelancer arbeitet direkt am Ergebnis, dafür ist die Kapazität begrenzt und Vertretung ein Thema, das offen besprochen gehört.",
            "Für klar abgegrenzte Vorhaben mit einem festen Ziel ist der direkte Weg meist schneller und günstiger.",
          ],
        },
        {
          h: "Preismodelle einordnen",
          p: [
            "Stundenbasis passt zu laufenden Anpassungen und schwer planbaren Aufgaben. Festpreis passt zu abgegrenzten Vorhaben mit definiertem Ergebnis.",
            "Bei mir sind Entwicklung und Anpassungen mit 110 € netto pro Stunde abgedeckt, Conversion-Projekte starten als Festpreis ab 3.990 € netto.",
          ],
        },
        {
          h: "Fünf Fragen vor der Beauftragung",
          p: [
            "Diese Fragen trennen belastbare Angebote von Versprechen.",
          ],
          list: [
            "Welche vergleichbaren Projekte gibt es, und darf ich sie ansehen?",
            "Wie entsteht die Aufwandsschätzung, und was passiert bei Abweichungen?",
            "Wem gehören Code und Zugänge nach Projektende?",
            "Wie sieht die Übergabe aus, und was ist dokumentiert?",
            "Wer ist erreichbar, wenn nach Abschluss etwas nicht funktioniert?",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-freelancer", label: "Shopify Freelancer" },
        { to: "/ratgeber/shopify-agentur-kosten", label: "Agentur oder Freelancer" },
        { to: "/shopify-entwickler", label: "Shopify Entwickler" },
        { to: "/preise", label: "Preise und Pakete" },
        { to: "/kontakt", label: "Projekt anfragen" },
      ]}
    />
  );
}
